import { signIn, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';

/**
 * Vendor-pool sign-in flow (pool-per-actor cutover, vendor build Phase 0 —
 * Rod 2026-09-17 §15gg N2). Mirrors the admin portal's employee-pool flow.
 *
 * The vendor pool is admin-create-only: an account exists because an
 * application or a team invite provisioned it, and Cognito emailed a
 * temporary password. A first sign-in walks temp password →
 * NEW_PASSWORD_REQUIRED → done. MFA is OPTIONAL on this pool, so TOTP stages
 * appear only for accounts that turned it on.
 */

export type LoginStage =
  | { kind: 'credentials' }
  | { kind: 'newPassword' }
  | { kind: 'totpSetup'; setupUri: string; secret: string }
  | { kind: 'totpCode' }
  | { kind: 'done' };

/** Thrown for flow states this portal doesn't support (SMS MFA, etc.). */
export class UnsupportedStepError extends Error {
  constructor(step: string) {
    super(`Unsupported sign-in step: ${step}`);
    this.name = 'UnsupportedStepError';
  }
}

interface AmplifyNextStep {
  signInStep: string;
  totpSetupDetails?: {
    sharedSecret: string;
    getSetupUri: (appName: string, accountName?: string) => URL;
  };
}

function toStage(nextStep: AmplifyNextStep, email: string): LoginStage {
  switch (nextStep.signInStep) {
    case 'DONE':
      return { kind: 'done' };
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { kind: 'newPassword' };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
      const details = nextStep.totpSetupDetails;
      if (!details) throw new UnsupportedStepError('TOTP_SETUP without setup details');
      return {
        kind: 'totpSetup',
        setupUri: details.getSetupUri('Grocery List Plus Vendor', email).toString(),
        secret: details.sharedSecret,
      };
    }
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'totpCode' };
    default:
      throw new UnsupportedStepError(nextStep.signInStep);
  }
}

/** Start a sign-in with email + password. */
export async function startSignIn(email: string, password: string): Promise<LoginStage> {
  try {
    const out = await signIn({ username: email, password });
    return toStage(out.nextStep as AmplifyNextStep, email);
  } catch (err) {
    // A live session from a previous visit — treat as signed in.
    if ((err as { name?: string })?.name === 'UserAlreadyAuthenticatedException') {
      return { kind: 'done' };
    }
    throw err;
  }
}

/** Answer the current challenge (new password, TOTP setup code, or TOTP code). */
export async function answerChallenge(response: string, email: string): Promise<LoginStage> {
  const out = await confirmSignIn({ challengeResponse: response });
  return toStage(out.nextStep as AmplifyNextStep, email);
}

/** True if a valid session already exists (skip the form entirely). */
export async function hasSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    return Boolean(session.tokens?.idToken);
  } catch {
    return false;
  }
}

/**
 * Validate the ?next= return path: same-origin relative paths only.
 * Anything absolute, protocol-relative, or empty falls back to "/".
 * usePortalGuard sends the FULL href; reduce it to its path first.
 */
export function safeNextPath(raw: string | null): string {
  if (!raw) return '/';
  let candidate = raw;
  if (/^https?:\/\//.test(raw)) {
    // An absolute href is only honoured when it is provably THIS origin.
    if (typeof window === 'undefined') return '/';
    try {
      const u = new URL(raw);
      if (u.origin !== window.location.origin) return '/';
      candidate = `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return '/';
    }
  }
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.includes('://')) return '/';
  if (candidate.startsWith('/login')) return '/';
  return candidate;
}
